// Web-only broadcasting studio: camera preview with overlaid stereo VU meter
// and mute, device pickers (hot-switchable while live), go-live/stop controls,
// live status + viewer count, share link.
// Media work happens in components/streaming/engine/; this file is UI.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Pressable, ActivityIndicator } from "react-native";
import tw from "twrnc";
import { Ionicons, MaterialCommunityIcons } from "@expo/vector-icons";
import Text from "@/components/shared/AppText";
import OnboardingButton from "@/components/shared/OnboardingButton";
import { UrlToWatch } from "@/components/shared/RtmpForm";
import CamStrip, { CamPreview } from "@/components/streaming/CamStrip";
import useBroadcastEngine from "@/components/hooks/useBroadcastEngine";
import {
  listDevices,
  isLocalRecordingSupported,
  chooseRecordingFile,
} from "@/components/streaming/engine/broadcastEngine";
import { allowedRelayOverride } from "@/components/streaming/relayUrl";
import { MULTICAM_ENABLED } from "@/components/streaming/config";
import AppConfig from "@/AppConfig";
import { t, tn } from "@/localization";

// `?relay=` is honoured only for our own relays, or on localhost and deploy
// previews (relayUrl.js).
const relayUrlFromEnv = () => {
  if (typeof window !== "undefined") {
    const override = new URLSearchParams(window.location.search).get("relay");
    const allowed = allowedRelayOverride(override, window.location.hostname);
    if (allowed) return allowed;
  }
  return AppConfig.STREAM_RELAY_URL;
};

// "Offline" is reserved for the machine actually having no network — the
// pre-live resting state is "Ready".
const STATUS = {
  idle: { label: t("Starting camera…"), color: "#999" },
  preview: { label: t("Ready"), color: "#999" },
  connecting: { label: t("Connecting…"), color: "#fbbf24" },
  publishing: { label: t("Connecting…"), color: "#fbbf24" }, // until acks/subscription confirm
  reconnecting: { label: t("Reconnecting…"), color: "#fbbf24" },
  paused: { label: t("Ready"), color: "#999" },
  ended: { label: t("Ready"), color: "#999" },
  error: { label: t("Error"), color: "#f87171" },
};

// The engine's own messages (components/streaming/engine/), in the user's
// language. Errors from the browser itself show as the browser wrote them.
const ENGINE_MESSAGES = {
  "H.264 encoding not supported on this device": t(
    "H.264 encoding not supported on this device"
  ),
  "Camera was interrupted — select it again or reload the page": t(
    "Camera was interrupted — select it again or reload the page"
  ),
  "no audio track": t("no audio track"),
};
const engineMessage = (message) =>
  Object.prototype.hasOwnProperty.call(ENGINE_MESSAGES, message)
    ? ENGINE_MESSAGES[message]
    : message;

const selectStyle = {
  flex: 1,
  minWidth: 140,
  padding: 8,
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#fff",
  fontSize: 14,
};

const LocalBubble = ({ stream, mirrored }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
      ref.current.play().catch(() => {});
    }
  }, [stream]);
  return (
    <video
      ref={ref}
      muted
      playsInline
      autoPlay
      style={{
        width: "100%",
        aspectRatio: "16/9",
        objectFit: "cover",
        transform: mirrored ? "scaleX(-1)" : "none",
        display: "block",
      }}
    />
  );
};

// Vertical stereo VU bars, drawn over the video next to the mute button so
// it's obviously about mic level (not connection strength).
const VuBars = ({ levels, muted }) => (
  <View style={[tw`flex-row items-end`, { gap: 3, height: 56 }]}>
    {levels.map((level, i) => (
      <View
        key={i}
        style={[
          tw`rounded-full overflow-hidden justify-end`,
          { width: 5, height: 56, backgroundColor: "rgba(255,255,255,0.25)" },
        ]}
      >
        <View
          style={{
            width: "100%",
            height: `${Math.round((muted ? 0 : level) * 100)}%`,
            backgroundColor: !muted && level > 0.85 ? "#f87171" : "#4ade80",
          }}
        />
      </View>
    ))}
  </View>
);

const BrowserStreamStudio = ({ event, streamKey, isTest, watchUrl }) => {
  const videoRef = useRef(null);
  // Multicam: the local camera/screen becomes CAM 1 (program audio); extra
  // cameras join via invite links and the mixer publishes the real key.
  const isSlotKey = /-CAM\d+$/.test(streamKey || "");
  const [camSlots, setCamSlots] = useState(0); // 0 = single camera mode
  const [mixStatus, setMixStatus] = useState(null); // { slots, mixing, activeInput }
  const multicam = MULTICAM_ENABLED && camSlots > 0 && !isSlotKey;
  const effectiveStreamKey = multicam ? `${streamKey}-CAM1` : streamKey;
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [videoDeviceId, setVideoDeviceId] = useState("");
  const [audioDeviceId, setAudioDeviceId] = useState("");
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  const [localStream, setLocalStream] = useState(null);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const {
    engine,
    state,
    stats,
    levels,
    muted,
    setMuted,
    recording,
    screenSharing,
    renewedStream,
    error,
    clearError,
  } = useBroadcastEngine({
    relayUrl: relayUrlFromEnv(),
    streamKey: effectiveStreamKey,
  });

  const mixerApi = useCallback(
    async (path, method = "GET") => {
      const res = await fetch(
        `${AppConfig.MIXER_CONTROL_BASE}/v1/mix/${streamKey}/${path}`,
        { method }
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || t("Mixer unavailable"));
      return body;
    },
    [streamKey]
  );

  // Adopt an in-progress multicam session on mount (page reload, other cams
  // already connected).
  useEffect(() => {
    if (!MULTICAM_ENABLED || !streamKey || isSlotKey) return;
    let stop = false;
    mixerApi("status")
      .then((st) => {
        if (stop) return;
        const maxReady = [4, 3, 2].find((n) => st.slots?.[n]) || 0;
        if (st.mixing || maxReady >= 2) {
          setCamSlots(Math.max(2, maxReady));
          setMixStatus(st);
        }
      })
      .catch(() => {});
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamKey, isSlotKey]);

  // Poll slot/mix state while multicam is on.
  useEffect(() => {
    if (!multicam) return;
    let stop = false;
    const poll = async () => {
      try {
        const st = await mixerApi("status");
        if (!stop) setMixStatus(st);
      } catch {}
    };
    poll();
    const timer = setInterval(poll, 3000);
    return () => {
      stop = true;
      clearInterval(timer);
    };
  }, [multicam, mixerApi]);

  // In multicam the local feed streams to its slot as soon as the preview is
  // up — the event only goes live when the mixer starts.
  useEffect(() => {
    if (multicam && state === "preview") {
      engine?.goLive().catch(() => {});
    }
  }, [multicam, state, engine]);

  // Engine re-acquired the capture (laptop sleep, device unplug) — swap the
  // preview to the fresh stream.
  useEffect(() => {
    if (renewedStream && videoRef.current) {
      videoRef.current.srcObject = renewedStream;
      videoRef.current.play().catch(() => {});
    }
  }, [renewedStream]);
  const canScreenShare =
    typeof navigator !== "undefined" &&
    Boolean(navigator.mediaDevices?.getDisplayMedia);

  const toggleScreenShare = async () => {
    try {
      const stream = screenSharing
        ? await engine.stopScreenShare()
        : await engine.startScreenShare();
      attachStream(stream);
    } catch (e) {
      if (e?.name === "AbortError") return; // user closed the picker
      // NotAllowedError is ambiguous: user cancel OR the OS blocking the
      // browser (macOS Screen Recording permission) — say so instead of
      // failing silently.
      setPreviewError(
        e?.name === "NotAllowedError"
          ? t(
              "Screen sharing didn't start. If you didn't cancel it yourself, allow Screen Recording for this browser in System Settings → Privacy & Security, then fully quit and reopen the browser."
            )
          : engineMessage(e?.message) || t("Could not share the screen.")
      );
    }
  };
  const [recFileName, setRecFileName] = useState(null);

  const toggleRecording = async () => {
    if (recording === "armed") {
      engine.setRecordingFile(null);
      setRecFileName(null);
      return;
    }
    const date = new Date().toISOString().slice(0, 10);
    const handle = await chooseRecordingFile(
      `${event?.slug || "stream"}-${date}.mp4`,
      t("MP4 video")
    );
    if (handle) {
      engine.setRecordingFile(handle);
      setRecFileName(handle.name);
    }
  };

  // Standalone test events go live via event_status; -TEST publishes flip
  // event_testing — accept either, and trust flowing acks (relay is feeding
  // ffmpeg's RTMP publish) as proof of being live even if the subscription
  // update is delayed.
  const serverLive =
    event?.event_status === "live" || Boolean(event?.event_testing);
  const acksFlowing = (stats?.uplinkKbps || 0) > 0;
  const isBroadcasting = ["connecting", "publishing", "reconnecting"].includes(
    state
  );
  // A streaming free test says TEST, never LIVE.
  const liveLabel = isTest ? t("TEST") : t("LIVE");
  const soloStatus =
    state === "publishing" && (serverLive || acksFlowing)
      ? { label: liveLabel, color: "#4ade80", live: true }
      : !online && !isBroadcasting
      ? { label: t("Offline"), color: "#f87171" }
      : STATUS[state] || STATUS.idle;
  const status = multicam
    ? mixStatus?.mixing && serverLive
      ? { label: liveLabel, color: "#4ade80", live: true }
      : mixStatus?.mixing
      ? { label: t("Connecting…"), color: "#fbbf24" }
      : !online
      ? { label: t("Offline"), color: "#f87171" }
      : state === "idle"
      ? STATUS.idle
      : { label: t("Ready"), color: "#999" }
    : soloStatus;

  // Any combination of publishing cameras can go live — never blocked on a
  // specific slot.
  const readySlots = Array.from({ length: camSlots }, (_, i) => i + 1).filter(
    (n) => mixStatus?.slots?.[n]
  );

  const addCamera = () => {
    if (!multicam) {
      if (
        isBroadcasting &&
        !window.confirm(
          t(
            "Switch to multi-camera? Your stream will restart as Camera 1 and go live again when you press Go Live."
          )
        )
      )
        return;
      setCamSlots(2);
    } else {
      setCamSlots((c) => Math.min(4, c + 1));
    }
  };
  const removeSlot = () => setCamSlots((c) => (c <= 2 ? 0 : c - 1));

  const startMix = async () => {
    clearError();
    setBusy(true);
    try {
      await mixerApi(`start?slots=${readySlots.join(",")}`, "POST");
      setMixStatus((st) => ({
        ...(st || {}),
        mixing: true,
        activeInput: readySlots.includes(1) ? 1 : readySlots[0],
      }));
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const stopMix = async () => {
    if (
      !window.confirm(
        isTest ? t("Stop your test?") : t("Stop your live stream?")
      )
    )
      return;
    setBusy(true);
    try {
      await mixerApi("stop", "POST");
      setMixStatus((st) => ({
        ...(st || {}),
        mixing: false,
        activeInput: null,
      }));
    } catch (e) {
      setPreviewError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const cutTo = async (n) => {
    setMixStatus((st) => (st ? { ...st, activeInput: n } : st)); // optimistic
    try {
      await mixerApi(`cut/${n}`, "POST");
    } catch (e) {
      setPreviewError(e.message);
    }
  };

  const attachStream = (stream) => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
    if (stream) setLocalStream(stream);
  };

  const startPreview = useCallback(
    async (video, audio) => {
      setPreviewError(null);
      try {
        const stream = await engine.startPreview({
          videoDeviceId: video || undefined,
          audioDeviceId: audio || undefined,
          videoElement: videoRef.current,
        });
        attachStream(stream);
        setDevices(await listDevices()); // labels available after permission
        const vId = stream.getVideoTracks()[0]?.getSettings().deviceId;
        const aId = stream.getAudioTracks()[0]?.getSettings().deviceId;
        if (vId) setVideoDeviceId(vId);
        if (aId) setAudioDeviceId(aId);
      } catch (e) {
        // Show what the browser actually sees — zero devices with permission
        // granted almost always means the OS is blocking the browser
        // (macOS System Settings → Privacy & Security → Camera/Microphone).
        let found = null;
        try {
          found = await listDevices();
        } catch {}
        setPreviewError(
          e?.name === "NotAllowedError"
            ? t(
                "Camera and microphone access was denied. Allow access in your browser settings and reload."
              )
            : e?.name === "NotFoundError"
            ? found
              ? t(
                  "No usable camera or microphone was found. Your browser detects {cameras} camera(s) and {mics} microphone(s). On a Mac, check System Settings → Privacy & Security → Camera and Microphone are enabled for this browser, then fully quit and reopen it.",
                  { cameras: found.cameras.length, mics: found.mics.length }
                )
              : t(
                  "No usable camera or microphone was found. On a Mac, check System Settings → Privacy & Security → Camera and Microphone are enabled for this browser, then fully quit and reopen it."
                )
            : e?.name === "NotReadableError"
            ? t(
                "The camera is in use by another app or tab. Close it and try again."
              )
            : engineMessage(e?.message) || t("Could not start the camera.")
        );
      }
    },
    [engine]
  );

  useEffect(() => {
    if (engine) startPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const onDeviceChange = async (kind, deviceId) => {
    const video = kind === "video" ? deviceId : videoDeviceId;
    const audio = kind === "audio" ? deviceId : audioDeviceId;
    if (kind === "video") setVideoDeviceId(deviceId);
    else setAudioDeviceId(deviceId);
    if (isBroadcasting) {
      try {
        const stream = await engine.setDevices({
          videoDeviceId: video || undefined,
          audioDeviceId: audio || undefined,
        });
        attachStream(stream);
      } catch (e) {
        setPreviewError(
          engineMessage(e?.message) || t("Could not switch device.")
        );
      }
    } else {
      await startPreview(video, audio);
    }
  };

  const goLive = async () => {
    clearError();
    setBusy(true);
    try {
      if (state === "paused") await engine.resumeLive();
      else await engine.goLive();
    } finally {
      setBusy(false);
    }
  };

  // Stop = back to standby (publish ends via the normal pause flow, preview
  // and connection stay warm so the user can go live again instantly).
  const stopLive = async () => {
    if (
      !window.confirm(
        isTest ? t("Stop your test?") : t("Stop your live stream?")
      )
    )
      return;
    setBusy(true);
    try {
      await engine.pause();
    } finally {
      setBusy(false);
    }
  };

  if (!engine) return <ActivityIndicator style={tw`m-10`} />;

  return (
    <View style={tw`w-full max-w-[720px] self-center px-4 pb-8`}>
      {/* Preview with VU + mute overlay */}
      <View style={tw`w-full rounded-xl overflow-hidden bg-black relative`}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            aspectRatio: "16/9",
            objectFit: screenSharing ? "contain" : "cover",
            transform: screenSharing ? "none" : "scaleX(-1)", // mirror cameras, never screens
            display: "block",
          }}
        />
        {multicam && mixStatus?.mixing && (mixStatus.activeInput || 1) > 1 && (
          <View style={[tw`absolute inset-0`, { backgroundColor: "#000" }]}>
            <CamPreview path={`cam/${streamKey}-CAM${mixStatus.activeInput}`} />
            <View
              style={[
                tw`absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded`,
                { backgroundColor: "rgba(248,65,35,0.9)" },
              ]}
            >
              <Text style={tw`text-white text-xs font-semibold`}>
                {t("CAM {number} · ON AIR", { number: mixStatus.activeInput })}
              </Text>
            </View>
            <Pressable
              onPress={() => cutTo(1)}
              style={[
                tw`absolute top-1.5 right-1.5 rounded-lg overflow-hidden`,
                {
                  width: 136,
                  borderWidth: 2,
                  borderColor: "rgba(255,255,255,0.85)",
                },
              ]}
            >
              <LocalBubble stream={localStream} mirrored={!screenSharing} />
              <View
                style={[
                  tw`absolute bottom-0 left-0 right-0 px-1 py-0.5`,
                  { backgroundColor: "rgba(0,0,0,0.55)" },
                ]}
              >
                <Text
                  style={tw`text-white text-center`}
                  numberOfLines={1}
                  adjustsFontSizeToFit
                >
                  {t("You · tap to go on air")}
                </Text>
              </View>
            </Pressable>
          </View>
        )}
        <View
          style={[
            tw`absolute bottom-2.5 left-2.5 flex-row items-end`,
            { gap: 8 },
          ]}
        >
          <Pressable
            onPress={() => setMuted(!muted)}
            style={[
              tw`w-10 h-10 rounded-full items-center justify-center`,
              {
                backgroundColor: muted
                  ? "rgba(220,38,38,0.85)"
                  : "rgba(0,0,0,0.5)",
              },
            ]}
          >
            <Ionicons name={muted ? "mic-off" : "mic"} size={20} color="#fff" />
          </Pressable>
          <VuBars levels={levels} muted={muted} />
        </View>
        <View style={[tw`absolute bottom-2.5 right-2.5 flex-row`, { gap: 8 }]}>
          {/* add-camera works everywhere, incl. mobile (no screenshare there) */}
          {MULTICAM_ENABLED && !isSlotKey && (
            <Pressable
              onPress={addCamera}
              style={[
                tw`w-10 h-10 rounded-full items-center justify-center`,
                {
                  backgroundColor: multicam
                    ? "rgba(248,65,35,0.9)"
                    : "rgba(0,0,0,0.5)",
                },
              ]}
            >
              <MaterialCommunityIcons
                name="video-plus"
                size={20}
                color="#fff"
              />
            </Pressable>
          )}
          {canScreenShare && screenSharing && (
            <Pressable
              onPress={() => engine.swapScreenShareLayout()}
              style={[
                tw`w-10 h-10 rounded-full items-center justify-center`,
                { backgroundColor: "rgba(0,0,0,0.5)" },
              ]}
            >
              <Ionicons name="swap-horizontal" size={20} color="#fff" />
            </Pressable>
          )}
          {canScreenShare && (
            <Pressable
              onPress={toggleScreenShare}
              style={[
                tw`w-10 h-10 rounded-full items-center justify-center`,
                {
                  backgroundColor: screenSharing
                    ? "rgba(248,65,35,0.9)"
                    : "rgba(0,0,0,0.5)",
                },
              ]}
            >
              <Ionicons
                name={screenSharing ? "stop-circle-outline" : "desktop-outline"}
                size={20}
                color="#fff"
              />
            </Pressable>
          )}
        </View>
      </View>

      {/* Camera tiles (multicam) */}
      {multicam && (
        <CamStrip
          baseKey={streamKey}
          slotCount={camSlots}
          status={mixStatus}
          mixing={Boolean(mixStatus?.mixing)}
          activeInput={mixStatus?.activeInput}
          onCut={cutTo}
          onRemoveSlot={removeSlot}
        />
      )}

      {/* Status row */}
      <View style={tw`flex-row items-center justify-between mt-3`}>
        <View style={tw`flex-row items-center`}>
          <View
            style={[
              tw`w-2.5 h-2.5 rounded-full mr-2`,
              { backgroundColor: status.color },
            ]}
          />
          <Text style={[tw`font-semibold`, { color: status.color }]}>
            {status.label}
          </Text>
          {serverLive && (
            <View style={tw`flex-row items-center ml-4`}>
              <Ionicons name="eye-outline" size={16} color="#666" />
              <Text style={tw`ml-1 text-sm text-gray-600`}>
                {event?.liveViewers ?? 0}
              </Text>
            </View>
          )}
          {recording === "recording" && (
            <View style={tw`flex-row items-center ml-4`}>
              <View
                style={[
                  tw`w-2 h-2 rounded-full mr-1`,
                  { backgroundColor: "#dc2626" },
                ]}
              />
              <Text style={tw`text-xs font-semibold text-red-600`}>REC</Text>
            </View>
          )}
        </View>
        {!multicam && status.live && stats && (
          <View style={tw`flex-row items-center`}>
            <Ionicons
              name={stats.quality === "poor" ? "cellular-outline" : "cellular"}
              size={16}
              color={stats.quality === "poor" ? "#f59e0b" : "#4ade80"}
            />
            <Text style={tw`ml-1 text-xs text-gray-500`}>
              {stats.uplinkKbps > 0
                ? `${stats.uplinkKbps} kbps`
                : `${stats.encoderKbps || 0} kbps`}
              {stats.quality === "poor" ? ` · ${t("poor signal")}` : ""}
            </Text>
          </View>
        )}
      </View>

      {previewError && (
        <View style={tw`py-2`}>
          <Text style={tw`text-red-600`}>{previewError}</Text>
          <Pressable
            onPress={() => startPreview(videoDeviceId, audioDeviceId)}
            style={tw`self-start mt-2 px-4 py-2 rounded-lg border border-gray-300`}
          >
            <Text>{t("Try Again")}</Text>
          </Pressable>
        </View>
      )}
      {error && (
        <Text style={tw`text-red-600 py-2`}>
          {error.code === "invalid_key"
            ? t("This stream link is invalid or expired.")
            : error.code === "publish_failed"
            ? t(
                "The ingest server rejected this stream. Check the event is purchased and inside its streaming window, and that nobody else is live."
              )
            : engineMessage(error.message) || String(error.code)}
        </Text>
      )}

      {/* Device pickers — switchable while live */}
      <View style={[tw`flex-row mt-3`, { gap: 8 }]}>
        <select
          style={selectStyle}
          value={videoDeviceId}
          onChange={(e) => onDeviceChange("video", e.target.value)}
        >
          {devices.cameras.length === 0 && (
            <option value="">{t("Camera")}</option>
          )}
          {devices.cameras.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || t("Camera {number}", { number: i + 1 })}
            </option>
          ))}
        </select>
        <select
          style={selectStyle}
          value={audioDeviceId}
          onChange={(e) => onDeviceChange("audio", e.target.value)}
        >
          {devices.mics.length === 0 && (
            <option value="">{t("Microphone")}</option>
          )}
          {devices.mics.map((d, i) => (
            <option key={d.deviceId} value={d.deviceId}>
              {d.label || t("Microphone {number}", { number: i + 1 })}
            </option>
          ))}
        </select>
      </View>

      {/* Local recording (desktop only) — armed before going live */}
      {isLocalRecordingSupported() && !isBroadcasting && (
        <Pressable
          onPress={toggleRecording}
          style={tw`flex-row items-center mt-3`}
        >
          <Ionicons
            name={recording === "armed" ? "checkbox" : "square-outline"}
            size={20}
            color={recording === "armed" ? "#F84123" : "#8B8B8D"}
          />
          <Text style={tw`ml-2 text-sm text-gray-700`}>
            {recording === "armed" && recFileName
              ? t("Will record to {file} (high quality)", { file: recFileName })
              : t("Also record to a local file (high quality)")}
          </Text>
        </Pressable>
      )}
      {recording === "saved" && (
        <Text style={tw`text-sm text-green-700 mt-2`}>
          {recFileName
            ? t("Recording saved to {file}.", { file: recFileName })
            : t("Recording saved.")}
        </Text>
      )}

      {/* Primary control */}
      <View style={tw`mt-4`}>
        {multicam ? (
          mixStatus?.mixing ? (
            <OnboardingButton
              text={t("Stop")}
              icon="stop-circle-outline"
              loading={busy}
              onPress={stopMix}
            />
          ) : (
            <OnboardingButton
              text={
                readySlots.length >= 1
                  ? isTest
                    ? tn(
                        "Start free test ({count} camera)",
                        "Start free test ({count} cameras)",
                        readySlots.length
                      )
                    : tn(
                        "Go Live ({count} camera)",
                        "Go Live ({count} cameras)",
                        readySlots.length
                      )
                  : t("Waiting for a camera…")
              }
              icon="videocam-outline"
              loading={busy}
              disabled={readySlots.length < 1}
              onPress={startMix}
            />
          )
        ) : (
          <>
            {(state === "preview" ||
              state === "paused" ||
              state === "error") && (
              <OnboardingButton
                text={isTest ? t("Start free test") : t("Go Live")}
                icon="videocam-outline"
                loading={busy}
                disabled={Boolean(previewError)}
                onPress={goLive}
              />
            )}
            {isBroadcasting && (
              <OnboardingButton
                text={t("Stop")}
                icon="stop-circle-outline"
                loading={busy}
                onPress={stopLive}
              />
            )}
          </>
        )}
      </View>

      {/* Share */}
      <View style={tw`mt-6`}>
        <UrlToWatch
          title={
            isTest
              ? t("Your private test link")
              : t("Link to Watch Your Live Event")
          }
          url={watchUrl}
        />
      </View>
    </View>
  );
};

export default BrowserStreamStudio;
